import type { AgreementDetails } from '@/api/schemas';
import { AgreementDocumentField } from '@/components/agreement/AgreementDocumentField';
import { AgreementStatusBadge } from '@/components/agreement/AgreementStatusBadge';
import { Field } from '@/components/Field';
import { Panel } from '@/components/ui/panel';
import { Separator } from '@/components/ui/separator';
import { formatMoney, NOT_AVAILABLE } from '@/lib/display';

const PAYMENT_DATA_UNAVAILABLE = 'Payment data temporarily unavailable';
const UNAVAILABLE = 'Unavailable';

export function AgreementHeader({
  agreement,
}: {
  agreement: AgreementDetails;
}) {
  const schedule = agreement.schedule;
  const contractCount = agreement.contracts.length;

  return (
    <section
      aria-labelledby="agreement-title"
      className="flex flex-col gap-4 lg:flex-row"
    >
      <div className="grid lg:w-[400px] lg:shrink-0">
        <Panel variant="data">
          <div className="flex flex-col gap-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-[20px] font-normal leading-6 text-black">
                  Agreement
                </p>
                <h1
                  id="agreement-title"
                  className="intl-headline-lg text-black"
                >
                  {agreement.agreementId}
                </h1>
              </div>
              {agreement.status ? (
                <AgreementStatusBadge status={agreement.status} />
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-4 text-[16px] leading-5">
              {schedule ? (
                <span className="font-medium text-[var(--intl-accent)]">
                  {schedule.customerName ?? NOT_AVAILABLE}
                </span>
              ) : (
                <span className="intl-text-secondary">
                  {PAYMENT_DATA_UNAVAILABLE}
                </span>
              )}
              <Separator orientation="vertical" className="h-4 bg-separator" />
              <span className="font-normal text-black">
                {contractCount} {contractCount === 1 ? 'contract' : 'contracts'}
              </span>
            </div>
            <Field
              label="Dealer Name & Location"
              value={agreement.dealerName ?? NOT_AVAILABLE}
              caption={agreement.dealershipId}
            />
          </div>
        </Panel>
      </div>

      <div className="grid flex-1">
        <Panel variant="data" size="wide">
          <div className="grid grid-cols-1 content-start gap-x-6 gap-y-4 sm:grid-cols-3">
            <Field
              label="Payment Frequency"
              value={
                agreement.paymentFrequencies.length > 0
                  ? agreement.paymentFrequencies.join(', ')
                  : NOT_AVAILABLE
              }
            />
            <Field
              label="Total Nr. of Payments"
              value={schedule ? schedule.payments.length : UNAVAILABLE}
            />
            <Field
              label="DNET"
              value={
                schedule
                  ? formatMoney(schedule.customerPayments.netTotal)
                  : UNAVAILABLE
              }
            />
          </div>
        </Panel>
      </div>

      <div className="grid lg:w-[220px] lg:shrink-0">
        <Panel variant="action" size="roomy">
          <div className="flex flex-col gap-6">
            <AgreementDocumentField
              agreementId={agreement.agreementId}
              party="customer"
              documentId={agreement.customerAgreementId}
            />
            <AgreementDocumentField
              agreementId={agreement.agreementId}
              party="dealer"
              documentId={agreement.dealerAgreementId}
            />
          </div>
        </Panel>
      </div>
    </section>
  );
}
